import React from 'react';
import {
  Paper,
  Badge,
  ActionIcon,
  Group,
  Text,
  Avatar,
  Anchor,
  Card,
  Stack,
  Tooltip,
} from '@mantine/core';
import {
  IconEye,
  IconCheck,
  IconX,
  IconTrash,
  IconBell,
  IconRefresh,
  IconUsersPlus,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { modals } from '@mantine/modals';
import { ReviewRequest, Competency, Swimlane } from '../../types';
import { useUserStore } from '../../store/userStore';
import { formatDateTime } from '../../utils/date';
import { getReviewStatusColor, getReviewStatusText } from '../../utils/statusUtils';
import { getReviewProgress, hasRequiredReviewers } from '../../utils/reviewProgress';

interface ReviewRequestsTableProps {
  title: string;
  requests: ReviewRequest[];
  showApplicant?: boolean;
  showReviewer?: boolean;
  onView: (request: ReviewRequest) => void;
  onApprove?: (requestId: string) => void;
  onReject?: (requestId: string) => void;
  onRemind?: (request: ReviewRequest) => void;
  onResubmit?: (request: ReviewRequest) => void;
  onAssignReviewers?: (request: ReviewRequest) => void;
  onDelete?: (request: ReviewRequest) => void;
  onOpenCompetencyDrawer?: (data: {
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  }) => void;
}

const initial = (name?: string) => (name ? name.charAt(0).toUpperCase() : '?');

/**
 * Список заявок на ревью в виде аккуратных карточек-строк.
 * Один компонент для всех вью (заявитель / ревьюер / админ).
 */
export const ReviewRequestsTable: React.FC<ReviewRequestsTableProps> = ({
  title,
  requests,
  showApplicant = false,
  showReviewer = false,
  onView,
  onApprove,
  onReject,
  onRemind,
  onResubmit,
  onAssignReviewers,
  onDelete,
  onOpenCompetencyDrawer,
}) => {
  const { t } = useTranslation();
  const meId = useUserStore((s) => s.user?._id);

  const getStatusColor = getReviewStatusColor;
  const getStatusText = (status: string) => getReviewStatusText(status, t);

  const handleOpenCompetencyDrawer = (
    competency: Competency,
    competencyMap: { _id: string; name: string },
  ) => {
    if (!onOpenCompetencyDrawer) return;
    const swimlane: Swimlane = { _id: competency.swimlaneId, title: competencyMap.name };
    onOpenCompetencyDrawer({ comp: competency, gradeIdx: 0, swimlane });
  };

  // «K/N одобрили» + «нужен ручной проверяющий» для pending-заявок.
  const renderProgress = (request: ReviewRequest) => {
    if (request.status !== 'pending') return null;
    return (
      <>
        {hasRequiredReviewers(request) &&
          (() => {
            const { approved, total } = getReviewProgress(request);
            return (
              <Tooltip label={t('reviewRequests.approvalProgressHint')}>
                <Badge variant="light" color="blue" radius="sm">
                  {approved}/{total}
                </Badge>
              </Tooltip>
            );
          })()}
        {request.needsManualReviewer && (
          <Tooltip label={t('reviewRequests.needsManualHint')}>
            <Badge variant="light" color="orange" radius="sm">
              {t('reviewRequests.needsManual')}
            </Badge>
          </Tooltip>
        )}
      </>
    );
  };

  // Тултип на кнопке «одобрить». С правом обхода — force (в обход единогласия).
  const approveTooltip = (request: ReviewRequest) => {
    if (request.canOverride) return t('reviewRequests.approveForce');
    return hasRequiredReviewers(request)
      ? t('reviewRequests.approveAllHint')
      : t('reviewRequests.approve');
  };

  // Право обхода одобряет в обход проверяющих — сразу и необратимо, поэтому переспрашиваем.
  const confirmForceApprove = (request: ReviewRequest) =>
    modals.openConfirmModal({
      title: t('reviewRequests.approveForce'),
      centered: true,
      children: (
        <Text size="sm">
          {t('reviewRequests.approveForceConfirm', { name: request.competency.title })}
        </Text>
      ),
      labels: { confirm: t('reviewRequests.approve'), cancel: t('common.cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: () => onApprove?.(request._id),
    });

  const renderActions = (request: ReviewRequest) => (
    <Group gap={6} wrap="nowrap">
      <Tooltip label={t('common.view')}>
        <ActionIcon variant="subtle" color="gray" radius="md" onClick={() => onView(request)}>
          <IconEye size={18} />
        </ActionIcon>
      </Tooltip>
      {request.status === 'pending' && request.canDecide === true && onApprove && onReject && (
        <>
          <Tooltip label={approveTooltip(request)}>
            <ActionIcon
              color="teal"
              variant="light"
              radius="md"
              aria-label={t('reviewRequests.approve')}
              onClick={() =>
                request.canOverride ? confirmForceApprove(request) : onApprove(request._id)
              }
            >
              <IconCheck size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('reviewRequests.reject')}>
            <ActionIcon
              color="red"
              variant="light"
              radius="md"
              aria-label={t('reviewRequests.reject')}
              onClick={() => onReject(request._id)}
            >
              <IconX size={18} />
            </ActionIcon>
          </Tooltip>
        </>
      )}
      {onRemind && request.status === 'pending' && (
        <Tooltip label={t('reviewRequests.remind')}>
          <ActionIcon variant="light" color="blue" radius="md" onClick={() => onRemind(request)}>
            <IconBell size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {onResubmit && request.status === 'rejected' && (
        <Tooltip label={t('reviewRequests.resubmit')}>
          <ActionIcon variant="light" color="teal" radius="md" onClick={() => onResubmit(request)}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {onAssignReviewers && request.status === 'pending' && (
        <Tooltip label={t('reviewRequests.assignReviewers')}>
          <ActionIcon
            variant="light"
            color="grape"
            radius="md"
            onClick={() => onAssignReviewers(request)}
          >
            <IconUsersPlus size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {/* Удалить: право с сервера (заявитель или competencies.review); свою — пока на ревью. */}
      {onDelete &&
        request.canDelete &&
        (request.status === 'pending' || request.applicant._id !== meId) && (
          <Tooltip label={t('common.delete')}>
            <ActionIcon
              variant="subtle"
              color="red"
              radius="md"
              aria-label={t('common.delete')}
              onClick={() => onDelete(request)}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        )}
    </Group>
  );

  const renderPersonMini = (
    person: { name?: string; email?: string; avatar?: string } | undefined,
    label: string,
  ) => (
    <Group gap={6} wrap="nowrap">
      <Avatar src={person?.avatar} radius="xl" size={20}>
        {initial(person?.name)}
      </Avatar>
      <Text size="xs" c="dimmed" truncate>
        <Text span size="xs" c="dimmed" fw={500}>
          {label}:
        </Text>{' '}
        {person?.name || '—'}
      </Text>
    </Group>
  );

  return (
    <Card p={{ base: 'sm', sm: 'md' }} mb="xl" withBorder radius="md">
      <Text size="lg" fw={600} mb="md">
        {title}
      </Text>

      <Stack gap="sm">
        {requests.map((request) => {
          const statusColor = getStatusColor(request.status);
          const leadPerson = showApplicant ? request.applicant : request.reviewer;
          return (
            <Paper
              key={request._id}
              withBorder
              radius="md"
              p="md"
              className="review-card"
              style={{
                borderLeft: `3px solid var(--mantine-color-${statusColor}-6)`,
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                <Group align="flex-start" wrap="nowrap" gap="sm" style={{ flex: 1, minWidth: 0 }}>
                  <Avatar src={leadPerson?.avatar} radius="xl" size="md">
                    {initial(leadPerson?.name)}
                  </Avatar>

                  <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Anchor
                      fw={600}
                      size="sm"
                      lineClamp={2}
                      onClick={() =>
                        handleOpenCompetencyDrawer(request.competency, request.competencyMap)
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      {request.competency.title}
                    </Anchor>

                    {/* Люди */}
                    <Group gap="md" wrap="wrap">
                      {showApplicant &&
                        renderPersonMini(request.applicant, t('reviewRequests.applicant'))}
                      {showReviewer &&
                        (request.reviewer ? (
                          renderPersonMini(request.reviewer, t('reviewRequests.reviewer'))
                        ) : (
                          <Text size="xs" c="dimmed">
                            {t('reviewRequests.pooledReviewers', {
                              count: request.reviewerPool?.length || 0,
                            })}
                          </Text>
                        ))}
                    </Group>

                    {/* Статус + прогресс + дата */}
                    <Group gap="xs" wrap="wrap">
                      <Badge color={statusColor} radius="sm">
                        {getStatusText(request.status)}
                      </Badge>
                      {renderProgress(request)}
                      <Text size="xs" c="dimmed">
                        {formatDateTime(request.createdAt)}
                      </Text>
                    </Group>

                    {/* Причина отказа */}
                    {request.status === 'rejected' && request.reviewerComment && (
                      <Text size="xs" c="red">
                        {t('reviewRequests.reviewerComment')}: {request.reviewerComment}
                      </Text>
                    )}
                  </Stack>
                </Group>

                {renderActions(request)}
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Card>
  );
};
